/**
 * A slot that has said its piece goes quiet for a while.
 *
 * Closing an ad retires the SLOT, not just that ad.
 *
 * ─── Two flavours, one set of functions ─────────────────────────────────────
 * Every slot calls the same three functions and names the flavour it wants:
 *
 *   SESSION  sessionStorage, holding '1'. Lasts until the tab closes.
 *   DATED    localStorage, holding an ISO expiry. Lasts a fixed number of days.
 *
 * Both are per-device. A cooldown set on a laptop does not apply on that teacher's
 * phone, which was accepted knowingly.
 */

export const SLOT_QUIET = {
  SESSION: 'session',
  DATED: 'dated',
};

export const SIDEBAR_SLOT_QUIET_KEY = 'sidebar_slot_quiet';
export const EXPORT_SLOT_QUIET_KEY = 'export_slot_quiet';
export const SNOOZE_KEY = 'in_app_ads_snoozed_until';
export const SNOOZE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

const FLAVOURS = {
  [SLOT_QUIET.SESSION]: {
    store: () => (typeof sessionStorage === 'undefined' ? null : sessionStorage),
    value: () => '1',
    isQuiet: (raw) => raw === '1',
  },
  [SLOT_QUIET.DATED]: {
    store: () => (typeof localStorage === 'undefined' ? null : localStorage),
    value: ({ days, now }) => new Date(now.getTime() + days * DAY_MS).toISOString(),
    // An unparseable value is NaN, and NaN > now is false — a corrupt cooldown
    // shows ads rather than hiding them forever.
    isQuiet: (raw, now) => Date.parse(raw ?? '') > now.getTime(),
  },
};

const withStore = (flavour, fallback, fn) => {
  try {
    const impl = FLAVOURS[flavour];
    const store = impl.store();
    return store ? fn(store, impl) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Quiet a slot.
 *
 * `days` applies to the DATED flavour only. `now` is injectable for the same
 * reason it is on isSlotQuiet: so a test need not fake timers.
 */
export const quietSlot = (flavour, key, { days = SNOOZE_DAYS, now = new Date() } = {}) =>
  withStore(flavour, undefined, (store, impl) => {
    store.setItem(key, impl.value({ days, now }));
  });

/** Whether a slot is currently quiet. A null key is never quiet. */
export const isSlotQuiet = (flavour, key, now = new Date()) =>
  key ? withStore(flavour, false, (store, impl) => impl.isQuiet(store.getItem(key), now)) : false;

// Module-free state, but tests still need to clear it between cases.
export const resetSlotQuietForTest = (flavour, key) =>
  withStore(flavour, undefined, (store) => {
    store.removeItem(key);
  });
